import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import CreateMembershipTypeAllowedTimeFormModal from 'src/features/allowedTimes/components/forms/membershipType/CreateMembershipTypeAllowedTimeFormModal';
import { allowedTimesMessages } from 'src/features/allowedTimes/utils/messages';
import { PERMISSION } from 'src/features/auth';
import { useGetMembershipTypeConfiguration } from 'src/features/membershipType/api/useGetMembershipTypeConfiguration';
import UpdateMembershipTypeFormModal from 'src/features/membershipType/components/forms/UpdateMembershipTypeFormModal';
import CreateZoneRuleFormModal from 'src/features/membershipType/components/forms/zoneRule/CreateZoneRuleFormModal';
import MembershipTemplateRequirement, {
    MEMBERSHIP_TEMPLATES_REQUIREMENTS,
} from 'src/features/membershipType/components/MembershipTemplateRequirement';
import { MembershipType } from 'src/features/membershipType/types';
import {
    membershipTypeMessages,
    restrictedGatewayMessages,
    zoneRulesMessages,
} from 'src/features/membershipType/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { GeneralBlock, GeneralBlockSubtitle, GeneralBlockTitle } from 'src/registry/components/blocks/GeneralBlock';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils/shadcn';

type MembershipTemplateConfigurationProps = {
    membershipTemplate: MembershipType;
};

const MembershipTemplateConfiguration = ({ membershipTemplate }: MembershipTemplateConfigurationProps) => {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const manageMembershipType = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIP_TYPES]);
    const accessPricingPlans = useHasPermissions([PERMISSION.ACCESS_PRICING_PLANS]);

    const ref = React.useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = React.useState(true);
    const [height, setHeight] = React.useState(0);

    const { data: templateConfigurationData } = useGetMembershipTypeConfiguration({
        membershipTypeId: membershipTemplate.id,
        parkingId,
    });

    React.useEffect(() => {
        if (!ref.current || !templateConfigurationData) return;

        const updateHeight = () => {
            setHeight(isOpen ? ref.current!.scrollHeight : 0);
        };

        updateHeight();

        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(ref.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isOpen, templateConfigurationData]);

    if (!templateConfigurationData) return null;

    const isTemplateValid = templateConfigurationData.valid;

    const generalBlockBackgroundColor = isTemplateValid ? 'bg-green-50' : 'bg-red-50';
    const generalBlockTextColor = isTemplateValid ? 'text-green-700' : 'text-red-700';
    const generalBlockBorderColor = isTemplateValid ? ' border border-green-200' : 'border border-red-200';
    const icon = isTemplateValid ? ICON_MAP.CHECK : ICON_MAP.CIRCLE_EXCLAMATION_SOLID;

    const totalRequiredRecords = Object.values(templateConfigurationData).reduce(
        (count, item) => (typeof item === 'object' && item !== null && item.required ? count + 1 : count),
        0,
    );
    const completedRequiredRecords = Object.values(templateConfigurationData).reduce(
        (count, item) => (typeof item === 'object' && item !== null && item.required && item.valid ? count + 1 : count),
        0,
    );

    const itemsToResolve = totalRequiredRecords - completedRequiredRecords;
    const title = isTemplateValid
        ? membershipTypeMessages.fullyConfigured
        : membershipTypeMessages.notConfiguredProperly;
    const subTitle = isTemplateValid
        ? formatMessage(membershipTypeMessages.fullyConfiguredDescription)
        : formatMessage(membershipTypeMessages.requiredRecordsToResolve, { count: itemsToResolve });

    const zoneRulesSubTitle = templateConfigurationData?.zone_rules?.custom_spaces_count
        ? formatMessage(membershipTypeMessages.zoneRulesRequirementCustomSpace)
        : formatMessage(membershipTypeMessages.zoneRulesRequirement, {
              count: membershipTemplate?.max_allowed_spaces_count ?? 0,
          });

    const zoneRulesRequirementSelectionText =
        !templateConfigurationData?.zone_rules?.required && templateConfigurationData?.zone_rules?.custom_spaces_count
            ? formatMessage(membershipTypeMessages.setPerMembership)
            : !templateConfigurationData?.zone_rules?.required &&
                !templateConfigurationData?.zone_rules?.custom_spaces_count
              ? formatMessage(membershipTypeMessages.noLimits)
              : `${formatMessage(membershipTypeMessages.pluralRules, { value: templateConfigurationData?.zone_rules?.zone_rules_count })} - ${formatMessage(membershipTypeMessages.zoneRulesSpaces, { value: `${templateConfigurationData?.zone_rules?.total_spaces_count}/${membershipTemplate?.max_allowed_spaces_count}` })}`;

    const pricingPlanId = templateConfigurationData.pricing_plan.pricing_plan?.id;

    const allowedTimesRequirementSelectionText =
        !templateConfigurationData?.allowed_times?.required &&
        templateConfigurationData?.allowed_times?.custom_time_restriction
            ? formatMessage(membershipTypeMessages.setPerMembership)
            : !templateConfigurationData.allowed_times.required
              ? formatMessage(membershipTypeMessages.noLimits)
              : templateConfigurationData.allowed_times?.valid
                ? formatMessage(membershipTypeMessages.totalAllowedTimes, {
                      count: templateConfigurationData.allowed_times?.allowed_times_count,
                  })
                : undefined;

    const allowedTimesSubtitle = templateConfigurationData.allowed_times.required
        ? formatMessage(membershipTypeMessages.allowedTimesRequirement)
        : templateConfigurationData.allowed_times.custom_time_restriction
          ? formatMessage(allowedTimesMessages.allowedTimesCanNotBeDefinedSubtitle)
          : formatMessage(membershipTypeMessages.allowedTimesRequirementOptional);

    return (
        <GeneralBlock className={cn('flex-1 gap-0 overflow-hidden p-0', generalBlockBorderColor)}>
            <div
                className={cn(
                    'flex min-h-20 items-center px-4 py-2 lg:min-h-10',
                    generalBlockBackgroundColor,
                    generalBlockTextColor,
                    isTemplateValid && 'cursor-pointer',
                )}
                onClick={() => (isTemplateValid ? setIsOpen((prev) => !prev) : undefined)}
            >
                <span className="grid w-full grid-cols-1 gap-2 text-sm lg:flex lg:flex-row lg:justify-between">
                    <div className="grid grid-cols-1 gap-2 xl:flex xl:flex-row">
                        <div className="flex flex-row gap-2">
                            <FontAwesomeIcon icon={icon} color={generalBlockTextColor} size="lg" />
                            <GeneralBlockTitle className={cn('font-bold', generalBlockTextColor)}>
                                {formatMessage(title)}
                            </GeneralBlockTitle>
                        </div>

                        <GeneralBlockSubtitle className={cn('font-light', generalBlockTextColor)}>
                            {subTitle}
                        </GeneralBlockSubtitle>
                    </div>

                    <Badge
                        theme="white"
                        className={cn(
                            'max-h-fit border font-bold opacity-75',
                            isTemplateValid ? 'border-green-400 text-green-700' : 'border-red-400 text-red-700',
                        )}
                    >
                        <div>
                            {formatMessage(membershipTypeMessages.requiredRecords, {
                                count: completedRequiredRecords,
                                totalCount: totalRequiredRecords,
                            })}
                        </div>
                    </Badge>
                </span>
            </div>

            <div
                className="overflow-hidden transition-[height] duration-500 ease-in-out"
                style={{ height: `${height}px` }}
            >
                <div ref={ref} className="px-4 py-4">
                    <div className="flex w-full flex-col justify-start gap-5">
                        <MembershipTemplateRequirement
                            title={formatMessage(pricingPlanMessages.pricingPlan)}
                            subTitle={formatMessage(membershipTypeMessages.pricingPlanRequirement)}
                            isRequired={templateConfigurationData.pricing_plan?.required}
                            isRequirementComplete={templateConfigurationData.pricing_plan?.valid}
                            requirementSelection={
                                templateConfigurationData.pricing_plan?.pricing_plan?.translations?.find(
                                    (plan) => plan.language === locale,
                                )?.name
                            }
                            requirementType={MEMBERSHIP_TEMPLATES_REQUIREMENTS.PRICING_PLAN}
                            children={
                                manageMembershipType ? (
                                    <UpdateMembershipTypeFormModal selectedMembershipTemplate={membershipTemplate}>
                                        <Button variant="destructiveLight" className="cursor-pointer">
                                            {formatMessage(membershipTypeMessages.configure)}
                                            <FontAwesomeIcon icon={ICON_MAP.ARROW_RIGHT} />
                                        </Button>
                                    </UpdateMembershipTypeFormModal>
                                ) : undefined
                            }
                            link={
                                accessPricingPlans && pricingPlanId
                                    ? `/${PATH.ADMINISTRATION}/pricing-plan/${pricingPlanId}`
                                    : undefined
                            }
                        />
                        <MembershipTemplateRequirement
                            title={formatMessage(zoneRulesMessages.zoneRules)}
                            subTitle={zoneRulesSubTitle}
                            isRequired={templateConfigurationData.zone_rules?.required}
                            isRequirementComplete={templateConfigurationData.zone_rules?.valid}
                            children={
                                manageMembershipType ? (
                                    <CreateZoneRuleFormModal
                                        children={
                                            <Button variant="destructiveLight" className="cursor-pointer">
                                                {formatMessage(membershipTypeMessages.configure)}
                                                <FontAwesomeIcon icon={ICON_MAP.ARROW_RIGHT} />
                                            </Button>
                                        }
                                    />
                                ) : undefined
                            }
                            requirementSelection={zoneRulesRequirementSelectionText}
                            requirementType={MEMBERSHIP_TEMPLATES_REQUIREMENTS.ZONE_RULES}
                            isCustom={templateConfigurationData.zone_rules.custom_spaces_count}
                        />
                        <MembershipTemplateRequirement
                            title={formatMessage(allowedTimesMessages.allowedTime)}
                            subTitle={allowedTimesSubtitle}
                            isRequired={templateConfigurationData.allowed_times?.required}
                            isRequirementComplete={templateConfigurationData.allowed_times?.valid}
                            children={
                                manageMembershipType ? (
                                    <CreateMembershipTypeAllowedTimeFormModal
                                        children={
                                            <Button variant="destructiveLight" className="cursor-pointer">
                                                {formatMessage(membershipTypeMessages.configure)}
                                                <FontAwesomeIcon icon={ICON_MAP.ARROW_RIGHT} />
                                            </Button>
                                        }
                                    />
                                ) : undefined
                            }
                            requirementType={MEMBERSHIP_TEMPLATES_REQUIREMENTS.ALLOWED_TIMES}
                            requirementSelection={allowedTimesRequirementSelectionText}
                            isCustom={templateConfigurationData.allowed_times.custom_time_restriction}
                        />
                        <MembershipTemplateRequirement
                            title={formatMessage(restrictedGatewayMessages.restrictedGateways)}
                            subTitle={formatMessage(membershipTypeMessages.restrictedGatewaysRequirement)}
                            isRequired={templateConfigurationData.restricted_gateways?.required}
                            isRequirementComplete={templateConfigurationData.restricted_gateways?.valid}
                            requirementType={MEMBERSHIP_TEMPLATES_REQUIREMENTS.RESTRICTED_GATEWAYS}
                            requirementSelection={
                                templateConfigurationData.restricted_gateways?.restricted_gateways?.length === 0
                                    ? formatMessage(membershipTypeMessages.allGatewaysAllowed)
                                    : formatMessage(membershipTypeMessages.pluralRestrictedGatewaysCount, {
                                          value: templateConfigurationData.restricted_gateways?.restricted_gateways
                                              ?.length,
                                      })
                            }
                        />
                    </div>
                </div>
            </div>
        </GeneralBlock>
    );
};

export default MembershipTemplateConfiguration;
